import React, { useState } from 'react';
import PropTypes from 'prop-types';
import InputAdornment from '@material-ui/core/InputAdornment';
import {
  Wrapper,
  Container,
  Panel,
  PanelBody,
  PanelFooter,
  PanelRooms,
  PanelBodyRooms,
  PanelButtonDates,
  PanelButtonRooms,
  PanelButtonFilters,
  RoomsInput,
  Backdrop,
  RoomsLabel,
  PriceRangeContainer,
  PriceRangeInputContainer,
  StyledInput,
  DistanceInput,
  DatesRoomsContainer,
  DescriptionContainer,
  PriceSectionContainer,
  FiltersSectionContainer
} from './Desktop.styles';
import { UNIT_OF_DISTANCE } from '../../../../../../constants/enums/DistanceUnits';
import SVGIcon from '../../../../../SVGIcon';
import FiltersContent from '../FiltersContent';
import Button from '../../../../../BuildingBlocks/Button';
import Text from '../../../../../BuildingBlocks/Text/Text';
import DatePicker from '../../../../../DatePicker';
import ToolTip from '../../../../../ToolTip/ToolTip';
import FormElements from '../../../../../FormElements/FormElements';
import { propTypes } from '../FiltersContainer.shapes';
import { getLabel, defaultProps, parseTemplate } from '../FiltersContainer.seed';
import { OPTION, LABEL } from '../FiltersContainer.constants';
import { colorBlue } from '../../../../../css/colors';
import { frenchMonthsMap } from './Desktop.staticContent';
import withCurrency from '../../../../../../hocs/withCurrency';

function FiltersContainerDesktop(props) {
  const {
    labels,
    selectedOption,
    onPanelButtonClick,
    showPanel,
    inset,
    show,
    onClearButtonClick,
    onUpdateFiltersButtonClick,
    numRooms,
    onRoomsValueChange,
    roomsInputProps,
    dateProps,
    onDatesChangeHook,
    location,
    distance,
    distanceProps,
    distanceUnitBefore,
    distanceUnitAfter,
    distanceMin,
    distanceMax,
    onDistanceChangeSlider,
    onDistanceChangeInput,
    onDistanceChangeInputSubmit,
    priceRangeMin,
    priceRangeMax,
    priceRangeSliderMin,
    priceRangeSliderMax,
    priceRangeStartIcon,
    priceRangeEndIcon,
    onPriceRangeChangeInput,
    onPriceRangeChangeSlider,
    onPriceRangeChangeInputSubmit,
    currency,
    showBackdrop,
    onBackdropClick
  } = props;

  const [selectedDistanceUnit, setSelectedDistanceUnit] = useState(
    currency !== 'US' ? UNIT_OF_DISTANCE.KILOMETERS : UNIT_OF_DISTANCE.MILES
  );

  const isPanelButtonSelected = value => selectedOption === value;

  const getFrenchDateString = template => {
    const [startDate, endDate] = template.split(' - ');
    const [startMonth, startDay] = startDate.split(' ');
    const [endMonth, endDay] = endDate.split(' ');
    const frenchStartMonth = frenchMonthsMap[startMonth];
    const frenchEndMonth = frenchMonthsMap[endMonth];
    return `${startDay} ${frenchStartMonth} - ${endDay} ${frenchEndMonth}`;
  };

  const parseDateString = template => {
    let frenchToggled;
    try {
      frenchToggled = window.localStorage.getItem('ljs-lang') === 'fr-CA';
    } catch (e) {
      frenchToggled = false;
    }

    return frenchToggled ? getFrenchDateString(template) : template;
  };

  const getDatesButtonLabel = () => {
    const { buttonDatesTemplate, dates } = LABEL;
    const template = parseTemplate(getLabel(labels, buttonDatesTemplate), props);
    const languageVerifiedTemplate = parseDateString(template);
    const top = getLabel(labels, dates);
    return (
      <>
        <Text.CopyMini color="quaternary" tagName="div" weight="bold" noMargins>
          {top}
        </Text.CopyMini>
        <div>
          <Text.HeadingFifth noMargins>{languageVerifiedTemplate}</Text.HeadingFifth>
        </div>
      </>
    );
  };

  const resolveDatePickerProps = () => {
    const { datesDisclaimer } = LABEL;
    return {
      ...dateProps,
      onDatesChangeHook,
      maxDays: 90,
      mapPage: true,
      disclaimerCopy: getLabel(labels, datesDisclaimer)
    };
  };

  const renderRoomsPanel = () => {
    const { ROOMS } = OPTION;
    const { roomsDisclaimerDesktop } = LABEL;

    return (
      <PanelRooms inset={inset} selectedOption={selectedOption} showing={show && showPanel && selectedOption === ROOMS}>
        <PanelBodyRooms>
          <Text.CopyMini color="secondary" noMargins>
            {getLabel(labels, roomsDisclaimerDesktop)}
          </Text.CopyMini>
        </PanelBodyRooms>
      </PanelRooms>
    );
  };

  const getRoomsButtonLabel = () => {
    const { rooms } = LABEL;
    const label = getLabel(labels, rooms);
    const inputProps = {
      inputProps: roomsInputProps,
      value: numRooms.toString(),
      onChange: onRoomsValueChange
    };
    return (
      <>
        <RoomsLabel tagName="div" color="quaternary" weight="bold" noMargins>
          {label}
        </RoomsLabel>
        <RoomsInput {...inputProps} />
      </>
    );
  };

  const getFiltersButtonLabel = selected => {
    const { buttonFilterTemplate } = LABEL;
    const label = parseTemplate(getLabel(labels, buttonFilterTemplate), props);
    return (
      <div className="text" style={{ display: 'flex', alignItems: 'center' }}>
        <Text.HeadingFifth>{label}</Text.HeadingFifth>
        <div style={{ margin: '8px 0 0 5px' }}>
          <SVGIcon selectedIcon="arrow" iconColor={colorBlue} rotate={selected ? 180 : 0} height="15px" width="15px" />
        </div>
      </div>
    );
  };

  const renderFiltersPanel = () => {
    const { FILTERS } = OPTION;
    const { clearButtonLabel, updateButtonLabel } = LABEL;
    return (
      <Panel inset={inset} selectedOption={selectedOption} showing={show && showPanel && selectedOption === FILTERS}>
        <PanelBody>
          <FiltersContent {...props} />
        </PanelBody>
        <PanelFooter>
          <Button theme="ghost" onClick={onClearButtonClick} fullWidth uppercase>
            {getLabel(labels, clearButtonLabel)}
          </Button>
          <Button onClick={onUpdateFiltersButtonClick} fullWidth uppercase>
            {getLabel(labels, updateButtonLabel)}
          </Button>
        </PanelFooter>
      </Panel>
    );
  };

  const renderBackdrop = () => showBackdrop && <Backdrop onClick={onBackdropClick} />;

  const renderDistanceSection = () => {
    const handleDistanceInputBlur = () => {
      onDistanceChangeInputSubmit();
    };

    const handleDistanceInputSubmitKeyPress = event => {
      if (event.key === 'Enter') {
        onDistanceChangeInputSubmit();
      }
    };

    const getDescription = () => {
      let sections = `${getLabel(labels, LABEL.distanceDescriptionTemplate)}`.split('{location}');
      const distanceStringIndex = sections.findIndex(item => item.search(/distance/) >= 0);

      if (distanceStringIndex >= 0) {
        const distanceStringClone = `${sections[distanceStringIndex]}`;
        const tempArr = distanceStringClone.split('{distance}');
        const distanceUnit = selectedDistanceUnit;

        const distanceInPreferredUnit =
          selectedDistanceUnit === UNIT_OF_DISTANCE.KILOMETERS ? distance : Math.ceil(distance / 1.60934); // Round up when converting to miles

        const inputProps = {
          value: distanceInPreferredUnit,
          onChange: onDistanceChangeInput,
          onBlur: handleDistanceInputBlur,
          onKeyDown: handleDistanceInputSubmitKeyPress
        };

        if (distanceUnitBefore) {
          inputProps.startAdornment = <InputAdornment position="start">{distanceUnit}</InputAdornment>;
        }
        if (distanceUnitAfter) {
          inputProps.endAdornment = (
            <InputAdornment position="end">
              <Text.CopyParagraph>{distanceInPreferredUnit}</Text.CopyParagraph>
              <FormElements.SwitchGroup
                selectedOption={selectedDistanceUnit}
                setSelectedOption={setSelectedDistanceUnit}
                options={[UNIT_OF_DISTANCE.KILOMETERS, UNIT_OF_DISTANCE.MILES]}
              />
            </InputAdornment>
          );
        }

        tempArr.splice(1, 0, key => <DistanceInput {...inputProps} key={key} />);

        sections[distanceStringIndex] = tempArr;
        sections = Array.prototype.concat(...sections); // Flatten
      }

      return (
        <>
          {sections
            .filter(Boolean)
            .map(Item =>
              typeof Item === 'function'
                ? Item
                : key => (
                    <Text.CopyParagraph tagName="span" key={key}>
                      {Item}
                    </Text.CopyParagraph>
                  )
            )
            .map((fn, index) => fn(index))}
        </>
      );
    };

    // Limit of characters - 21, hiding overflow by restricting to 17 and adding ellipsis.
    const formattedLocation = location.length >= 21 ? `${location.slice(0, 17)}...` : location;

    return (
      <DescriptionContainer>
        <Text.HeadingFifth noMargins weight="bold">
          {getLabel(labels, LABEL.distanceTitle)}
        </Text.HeadingFifth>
        <div style={{ padding: '1px 0 2px 0' }}>
          <Text.CopyParagraph noMargins tagName="div">
            {getDescription()}
          </Text.CopyParagraph>
        </div>
        <FormElements.Slider
          value={distance}
          min={distanceMin}
          max={distanceMax}
          onChange={onDistanceChangeSlider}
          {...distanceProps}
        />
        {location.length >= 21 ? (
          <ToolTip content={location}>
            <Text.CopyMini tagName="span" weight="bold">
              (of {formattedLocation})
            </Text.CopyMini>
          </ToolTip>
        ) : (
          <Text.CopyMini tagName="span" weight="bold">
            (of {formattedLocation})
          </Text.CopyMini>
        )}
      </DescriptionContainer>
    );
  };

  const renderPriceRangeSection = () => {
    const handlePriceInputBlur = () => {
      onPriceRangeChangeInputSubmit();
    };

    const handlePriceInputSubmitKeyPress = event => {
      if (event.key === 'Enter') {
        onPriceRangeChangeInputSubmit();
      }
    };

    const handleInputChange = origin => event => {
      if (onPriceRangeChangeInput) {
        const result = [];
        const { value } = event.target;
        if (origin === 'min') {
          result.splice(0, 0, value, priceRangeMax);
        } else {
          result.splice(0, 0, priceRangeMin, value);
        }
        onPriceRangeChangeInput(result, origin);
      }
    };

    const inputProps = {};
    if (priceRangeStartIcon) {
      inputProps.startAdornment = <InputAdornment position="start">{priceRangeStartIcon}</InputAdornment>;
    }
    if (priceRangeEndIcon) {
      inputProps.endAdornment = <InputAdornment position="end">{priceRangeEndIcon}</InputAdornment>;
    }

    return (
      <PriceSectionContainer>
        <div>
          <Text.HeadingFifth noMargins weight="bold">
            {getLabel(labels, LABEL.priceRangeTitle)}
          </Text.HeadingFifth>
        </div>
        <PriceRangeContainer>
          <PriceRangeInputContainer>
            <StyledInput
              noMargins
              {...inputProps}
              value={priceRangeMin}
              onChange={handleInputChange('min')}
              onBlur={handlePriceInputBlur}
              onKeyDown={handlePriceInputSubmitKeyPress}
            />
            <Text.CopyBody noMargins weight="bold">
              -
            </Text.CopyBody>
            <StyledInput
              noMargins
              {...inputProps}
              value={priceRangeMax}
              onChange={handleInputChange('max')}
              onBlur={handlePriceInputBlur}
              onKeyDown={handlePriceInputSubmitKeyPress}
            />
          </PriceRangeInputContainer>
        </PriceRangeContainer>

        <FormElements.Slider
          isRange
          value={[priceRangeMin, priceRangeMax]}
          min={priceRangeSliderMin}
          max={priceRangeSliderMax}
          onChange={onPriceRangeChangeSlider}
        />
        <Text.CopyMini noMargins weight="bold">
          {getLabel(labels, LABEL.priceRangeDescription)}
        </Text.CopyMini>
      </PriceSectionContainer>
    );
  };

  return (
    <Wrapper visible={show}>
      {renderBackdrop()}
      <Container inset={inset}>
        <DatesRoomsContainer>
          <div style={{ width: '70%' }}>
            <DatePicker {...resolveDatePickerProps()}>
              {({ openPicker, closePicker, visible }) => (
                <PanelButtonDates
                  selected={isPanelButtonSelected(OPTION.DATES)}
                  onClick={() => {
                    onPanelButtonClick(OPTION.DATES);
                    !visible ? openPicker() : closePicker();
                  }}
                >
                  {getDatesButtonLabel()}
                </PanelButtonDates>
              )}
            </DatePicker>
          </div>
          <PanelButtonRooms
            selected={isPanelButtonSelected(OPTION.ROOMS)}
            onClick={() => {
              onPanelButtonClick(OPTION.ROOMS);
            }}
          >
            {getRoomsButtonLabel()}
          </PanelButtonRooms>
        </DatesRoomsContainer>
        <FiltersSectionContainer>
          {renderPriceRangeSection()}
          <div style={{ justifyItems: 'right', width: '50%' }}>
            {renderDistanceSection()}
            <div style={{ width: '100%', display: 'flex', flexDirection: 'row-reverse' }}>
              <PanelButtonFilters
                selected={isPanelButtonSelected(OPTION.FILTERS)}
                onClick={() => {
                  onPanelButtonClick(OPTION.FILTERS);
                }}
              >
                {getFiltersButtonLabel(isPanelButtonSelected(OPTION.FILTERS))}
              </PanelButtonFilters>
            </div>
          </div>
        </FiltersSectionContainer>
      </Container>
      {renderRoomsPanel()}
      {renderFiltersPanel()}
    </Wrapper>
  );
}

FiltersContainerDesktop.propTypes = {
  ...propTypes,
  show: PropTypes.bool.isRequired
};

FiltersContainerDesktop.defaultProps = defaultProps;

export default withCurrency(FiltersContainerDesktop);
